// © Microsoft Corporation. All rights reserved.

import { appEnvironment, localStorage } from "@msnews/core";
import { buildVersionKey, locationHref } from "./Constants";

/**
 * Force timestamp prefix for render cache dom
 */
const forceTimestampPrefix = `force_timestamp_`;

/**
 * Force timestamp key for render cache dom
 */
export const forceStorageTimestampKey = `${forceTimestampPrefix}${locationHref}`;

/**
 * render-cache force expiration time in ms
 * 129600000 = 36 hours in milliseconds
 */
export const forceExpirationTimeMs = 129600000;

/**
 * Function to determine if cache refresh should be forced.
 * Used as failsafe to force refresh of Render cache in case of corrupted render cache loop.
 * @returns true if we need to force refresh of cache.
 */
export function IsForceTimestampExpired(): boolean {
    const currentTimestamp: number = new Date().getTime();
    const forceTimestamp = localStorage.getItem(forceStorageTimestampKey);
    const parsedForceTimestamp = forceTimestamp && parseInt(forceTimestamp);

    if (!parsedForceTimestamp) {
        localStorage.setObject(forceStorageTimestampKey, currentTimestamp);
        return false;
    }

    if ((currentTimestamp - parsedForceTimestamp) < forceExpirationTimeMs) {
        return false; // Force Cache Timestamp is valid.
    }

    return true; // Force Cache Timestamp expired.
}

/**
 * Determines if build version seen in current response is same as the stored build version corresponding to current render cached page.
 * Used as a mechanism to force refresh render cache whenever there is a new deployment to avoid livesite issues.
 * @returns true if build versions are same.
 */
export function isBuildVersionSame(): boolean {
    const currentBuildVer = appEnvironment.BuildVersion;
    const storedBuildVer = localStorage.getItem(buildVersionKey);

    // If there was no previously stored build or PCS does not return the build version in the response,
    // return false since we don't want to persist render cache without the two builds to compare
    if (!storedBuildVer || !currentBuildVer || storedBuildVer !== currentBuildVer) {
        return false;
    }

    return true;
}